import React, { useState, useEffect } from 'react';
import { Button } from "components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card"
import { Input } from "components/ui/input"
import { Label } from "components/ui/label"
import { auth } from 'firebase-config';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Building2, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';

export function SignUpPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignUp = async () => {
    // Clear previous errors
    setError('');

    // Validation
    if (!displayName || !email || !password || !confirmPassword) {
      setError('Per favore inserisci tutti i campi');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Per favore inserisci un indirizzo email valido');
      return;
    }

    if (password.length < 6) {
      setError('La password deve avere alemeno 6 caratteri');
      return;
    }

    if (password !== confirmPassword) {
      setError('Le passwords non corrispondono');
      return;
    }

    setLoading(true);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Update the user's profile with the display name
      await updateProfile(userCredential.user, { displayName });
      
      // Force token refresh to include displayName in custom claims
      await userCredential.user.getIdToken(true);
      
      // Navigation will be handled by useEffect when user state changes
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Account già esiste esistente');
      } else if (err.code === 'auth/invalid-email') {
        setError('Per favore inserisci un indirizzo email valido');
      } else if (err.code === 'auth/weak-password') {
        setError('Password troppo debole. Inserisci una password migliore');
      } else {
        setError('Errore creazione account. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSignUp();
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center gap-2 justify-center mb-4">
            <Building2 className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold text-gray-900">ReCondo</span>
          </Link>
          <CardTitle className="text-2xl">Crea il tuo account</CardTitle>
          <CardDescription>
            Inserisci le tue informazioni per creare il tuo account
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="display-name">Nome</Label>
              <Input
                id="display-name"
                placeholder="Max Robinson"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">Conferma Password</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Sign Up Failed</AlertTitle>
                <AlertDescription>
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={handleSignUp} disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Crea il tuo account
            </Button>
          </div>

          <div className="mt-4 text-center text-sm">
            Hai già un account?{" "}
            <Link to="/login" className="underline">
              Accedi
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 